import { z } from 'zod';

/**
 * Case Wiki — taxonomy of party **civil quality** (qualité civile/procédurale).
 *
 * Axe orthogonal à `strategic_side` (notre client / adverse / tiers / unset) :
 *   - `strategic_side` répond à "qui est qui dans le procès" (rôle stratégique).
 *   - `civil_quality` (ce module) répond à "quelle est la qualité civile ou
 *     procédurale de cette partie" (employeur, prévenu, créancier, époux…).
 *
 * Le catalogue est **contextuel à la branche du droit** (`cases.procedure_type`) :
 * un dossier de droit social a un vocabulaire ("salarié", "employeur") qui n'a
 * rien à voir avec un dossier pénal ("prévenu", "partie civile"). On expose les
 * catalogues par branche pour permettre des dropdowns filtrés et un prompt
 * agent contextualisé.
 *
 * Liste fermée par branche + `'autre'` partout pour les cas exotiques (curateur,
 * séquestre, expert judiciaire commis…). Si une valeur du catalogue ne convient
 * pas, l'agent doit retourner `'autre'` plutôt qu'inventer.
 *
 * Source unique consommée par :
 *   - le prompt système ingest (exemples au modèle, contextualisé par procedure_type)
 *   - les badges UI (label + tooltip)
 *   - le filtre par civil_quality dans DashboardParties
 *
 * Tableau des qualités collecté avec un avocat de 14 ans de barreau (review
 * métier 2026-04-30) — couvre 80 % des cas usuels par branche.
 */

// ---------------------------------------------------------------------------
// Branches du droit — source unique des sélecteurs « Type de procédure »
// (CaseFormDialog, CaseEditDialog, CaseProcedureTypeField).
// ---------------------------------------------------------------------------

/**
 * Valeurs proposées pour `cases.procedure_type` (colonne `text` libre, sans
 * CHECK en base : cette liste est la seule borne).
 *
 * « Droit Immobilier et de la Construction » (2026-09-21) reprend à la lettre la
 * `specialisation` des 102 actes immobiliers du catalogue (`catalog-data.ts`,
 * picker Word) : la matière du dossier sert de filtre par défaut au catalogue
 * d'actes de l'agent, un libellé abrégé (« Droit immobilier ») ne matcherait
 * aucun acte. Aucune qualité civile dédiée pour l'instant : un dossier
 * immobilier ne propose que `autre` via `getCivilQualitiesForProcedure()`.
 *
 * « Droit des Assurances et de la Responsabilité Civile » (2026-10-28), même règle : libellé exact de
 * `ACT_SPECIALIZATION_LABELS.assurances`, sinon l'agent ne trouve aucun des 9 actes de la matière.
 *
 * « Médiation » (2026-11-03, Benoît) : aucun acte du catalogue ne porte cette `specialisation`,
 * l'agent rend donc le sommaire des matières plutôt qu'un filtre vide ; pas de qualité dédiée.
 */
export const PROCEDURE_TYPES = [
  'Droit Pénal',
  'Droit Commercial',
  'Droit Social',
  'Droit Civil',
  'Droit de la Famille',
  'Droit Public',
  'Droit Immobilier et de la Construction',
  'Médiation',
  'Droit des Assurances et de la Responsabilité Civile',
] as const;
export type ProcedureType = (typeof PROCEDURE_TYPES)[number];

/**
 * Valeur canonique de `cases.procedure_type` pour la matière pénale. Sert de
 * discriminateur de matière (bascule du rail Procédure, tri des parties au
 * pénal…). Centralisé ici pour éviter la string magique dupliquée côté front.
 */
export const PROCEDURE_TYPE_PENAL: ProcedureType = 'Droit Pénal';

// ---------------------------------------------------------------------------
// Enum
// ---------------------------------------------------------------------------

export const CivilQualitySchema = z.enum([
  // — Droit Social —
  'salarie',
  'employeur',
  'ancien_salarie',
  'syndicat',
  'cse',

  // — Droit Pénal —
  'prevenu',
  'accuse',
  'partie_civile',
  'temoin_assiste',
  'mis_en_examen',
  'civilement_responsable',
  'ministere_public',

  // — Droit Civil —
  'demandeur',
  'defendeur',
  'intervenant_volontaire',
  'appele_en_garantie',
  'tiers_opposant',

  // — Droit Commercial —
  'creancier',
  'debiteur',
  'caution',
  'mandataire_liquidateur',
  'dirigeant',
  'associe_minoritaire',
  'associe_majoritaire',

  // — Droit de la Famille —
  'epoux_demandeur',
  'epoux_defendeur',
  'parent',
  'enfant_represente',
  'heritier',

  // — Droit Public —
  'requerant',
  'administration_defenderesse',
  'partie_interessee',

  // — Catch-all —
  'autre',
]);
export type CivilQuality = z.infer<typeof CivilQualitySchema>;

// ---------------------------------------------------------------------------
// Per-value metadata — single source of truth
// ---------------------------------------------------------------------------

export interface CivilQualityInfo {
  /** Short label rendered in the badge / dropdown. */
  label: string;
  /** Branche du droit où cette qualité est usuellement employée. */
  procedureType: ProcedureType | 'transverse';
  /** Tooltip text. Doubles as LLM exemplar in the ingest prompt. */
  description: string;
}

export const CIVIL_QUALITY_INFO: Record<CivilQuality, CivilQualityInfo> = {
  // — Droit Social —
  salarie: {
    label: 'Salarié',
    procedureType: 'Droit Social',
    description:
      "Personne liée par un contrat de travail (en cours ou rompu) dans un litige avec son employeur.",
  },
  employeur: {
    label: 'Employeur',
    procedureType: 'Droit Social',
    description:
      "Personne morale ou physique qui emploie ou a employé le salarié — défendeur typique en prud'hommes.",
  },
  ancien_salarie: {
    label: 'Ancien salarié',
    procedureType: 'Droit Social',
    description:
      "Salarié dont le contrat a pris fin avant le contentieux (licenciement, démission, rupture conventionnelle).",
  },
  syndicat: {
    label: 'Organisation syndicale',
    procedureType: 'Droit Social',
    description:
      "Syndicat agissant en intervention ou pour la défense des intérêts collectifs.",
  },
  cse: {
    label: 'CSE',
    procedureType: 'Droit Social',
    description:
      "Comité Social et Économique en action collective (information-consultation, expertise, droit d'alerte).",
  },

  // — Droit Pénal —
  prevenu: {
    label: 'Prévenu',
    procedureType: 'Droit Pénal',
    description:
      "Personne poursuivie devant le tribunal correctionnel pour un délit. Présumée innocente.",
  },
  // Ajouté le 2026-10-08. Le prompt d'ingestion demandait déjà `civil_quality='accuse'` et le
  // reste du code le lisait (`partyDisplay.ts`, scan lint des statuts non sourcés) : seule
  // l'énumération l'ignorait, donc chaque accusé provoquait un refus de l'outil d'écriture.
  accuse: {
    label: 'Accusé',
    procedureType: 'Droit Pénal',
    description:
      "Personne renvoyée devant la cour d'assises ou la cour criminelle départementale pour un crime. Présumée innocente.",
  },
  partie_civile: {
    label: 'Partie civile',
    procedureType: 'Droit Pénal',
    description:
      "Victime d'une infraction qui se constitue partie civile pour obtenir réparation au pénal.",
  },
  temoin_assiste: {
    label: 'Témoin assisté',
    procedureType: 'Droit Pénal',
    description:
      "Statut intermédiaire entre témoin et mis en examen — droit à un avocat, pas de présomption d'innocence formelle.",
  },
  mis_en_examen: {
    label: 'Mis en examen',
    procedureType: 'Droit Pénal',
    description:
      "Personne contre qui il existe des indices graves ou concordants d'avoir participé à une infraction (instruction).",
  },
  civilement_responsable: {
    label: 'Civilement responsable',
    procedureType: 'Droit Pénal',
    description:
      "Personne tenue de répondre civilement des dommages causés par un tiers (parents pour mineur, employeur pour préposé).",
  },
  ministere_public: {
    label: 'Ministère public',
    procedureType: 'Droit Pénal',
    // Le Parquet porte l'action publique : c'est l'adversaire PRINCIPAL d'un
    // accusé/prévenu/mis en examen (≠ la partie civile, adversaire secondaire).
    // Cette qualité sert de signal pour dériver le rang principal/secondaire à
    // l'affichage (cf. spec 2026-06-05-case-wiki-penal). Porté par une partie
    // institution (Parquet, PNF, procureur de la République…), quel que soit son
    // nom propre — c'est `civil_quality` qui tranche le rang, pas le slug.
    description:
      "Parquet / procureur de la République / procureur général — autorité qui exerce l'action publique. Adversaire principal du prévenu, mis en examen ou accusé.",
  },

  // — Droit Civil —
  demandeur: {
    label: 'Demandeur',
    procedureType: 'Droit Civil',
    description:
      "Partie qui saisit la juridiction et formule des prétentions (assignation, requête).",
  },
  defendeur: {
    label: 'Défendeur',
    procedureType: 'Droit Civil',
    description:
      "Partie contre qui l'action est dirigée — répond aux prétentions du demandeur.",
  },
  intervenant_volontaire: {
    label: 'Intervenant volontaire',
    procedureType: 'Droit Civil',
    description:
      "Tiers qui intervient spontanément dans une instance en cours pour soutenir une partie ou faire valoir un droit.",
  },
  appele_en_garantie: {
    label: 'Appelé en garantie',
    procedureType: 'Droit Civil',
    description:
      "Tiers attrait par une partie pour qu'il garantisse une condamnation éventuelle (assureur, vendeur, sous-traitant).",
  },
  tiers_opposant: {
    label: 'Tiers opposant',
    procedureType: 'Droit Civil',
    description:
      "Tiers qui forme tierce opposition contre une décision rendue sans qu'il soit partie au procès.",
  },

  // — Droit Commercial —
  creancier: {
    label: 'Créancier',
    procedureType: 'Droit Commercial',
    description:
      "Titulaire d'une créance — déclare au passif d'une procédure collective ou agit en recouvrement.",
  },
  debiteur: {
    label: 'Débiteur',
    procedureType: 'Droit Commercial',
    description:
      "Personne qui doit la créance — défendeur en recouvrement, ou société sous procédure collective.",
  },
  caution: {
    label: 'Caution',
    procedureType: 'Droit Commercial',
    description:
      "Personne qui garantit une dette d'autrui — peut être appelée en paiement subsidiaire.",
  },
  mandataire_liquidateur: {
    label: 'Mandataire / liquidateur',
    procedureType: 'Droit Commercial',
    description:
      "Mandataire judiciaire ou liquidateur représentant les créanciers dans une procédure collective.",
  },
  dirigeant: {
    label: 'Dirigeant',
    procedureType: 'Droit Commercial',
    description:
      "Représentant légal d'une société (président, gérant, DG) attrait à titre personnel — action en responsabilité ou faute détachable.",
  },
  associe_minoritaire: {
    label: 'Associé minoritaire',
    procedureType: 'Droit Commercial',
    description:
      "Associé détenant moins de 50 % du capital — typique en abus de majorité, action ut singuli.",
  },
  associe_majoritaire: {
    label: 'Associé majoritaire',
    procedureType: 'Droit Commercial',
    description:
      "Associé détenant la majorité du capital — défendeur typique en abus de majorité.",
  },

  // — Droit de la Famille —
  epoux_demandeur: {
    label: 'Époux demandeur',
    procedureType: 'Droit de la Famille',
    description:
      "Époux qui introduit la procédure (divorce, séparation, contribution).",
  },
  epoux_defendeur: {
    label: 'Époux défendeur',
    procedureType: 'Droit de la Famille',
    description:
      "Époux contre qui la procédure est introduite — répond à l'assignation ou à la requête.",
  },
  parent: {
    label: 'Parent',
    procedureType: 'Droit de la Famille',
    description:
      "Titulaire de l'autorité parentale dans un litige relatif à l'enfant (résidence, droit de visite, contribution).",
  },
  enfant_represente: {
    label: 'Enfant représenté',
    procedureType: 'Droit de la Famille',
    description:
      "Enfant mineur dont les intérêts sont en cause — représenté par ses parents ou un administrateur ad hoc.",
  },
  heritier: {
    label: 'Héritier',
    procedureType: 'Droit de la Famille',
    description:
      "Personne ayant vocation à hériter — partie typique en succession contentieuse.",
  },

  // — Droit Public —
  requerant: {
    label: 'Requérant',
    procedureType: 'Droit Public',
    description:
      "Personne qui introduit un recours devant une juridiction administrative (REP, plein contentieux).",
  },
  administration_defenderesse: {
    label: 'Administration défenderesse',
    procedureType: 'Droit Public',
    description:
      "Personne publique (État, collectivité, établissement) dont l'acte ou la décision est attaqué.",
  },
  partie_interessee: {
    label: 'Partie intéressée',
    procedureType: 'Droit Public',
    description:
      "Tiers ayant un intérêt à l'annulation ou au maintien de l'acte attaqué — peut intervenir.",
  },

  // — Catch-all —
  autre: {
    label: 'Autre',
    procedureType: 'transverse',
    description:
      "Qualité non rattachable au catalogue (curateur, séquestre, expert commis, mandataire ad hoc…). À préférer plutôt qu'inventer.",
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Catalogue contextuel : pour un `procedure_type` donné, retourne les
 * qualités usuelles de cette branche + `'autre'`. Utilisé pour filtrer le
 * dropdown UI et le prompt agent.
 */
export function getCivilQualitiesForProcedure(
  procedureType: ProcedureType | string | null | undefined,
): CivilQuality[] {
  const matched = (Object.entries(CIVIL_QUALITY_INFO) as Array<[CivilQuality, CivilQualityInfo]>)
    .filter(([, info]) => info.procedureType === procedureType)
    .map(([k]) => k);
  // Toujours `autre` en fin de liste.
  return [...matched, 'autre' as CivilQuality];
}

/**
 * Toutes les qualités groupées par branche du droit — pour une vue catalogue
 * ou un dropdown sectionné quand le `procedure_type` est inconnu.
 */
export function groupCivilQualitiesByProcedure(): Array<{
  procedureType: ProcedureType | 'transverse';
  qualities: CivilQuality[];
}> {
  const acc = new Map<ProcedureType | 'transverse', CivilQuality[]>();
  for (const [k, info] of Object.entries(CIVIL_QUALITY_INFO) as Array<
    [CivilQuality, CivilQualityInfo]
  >) {
    const arr = acc.get(info.procedureType) ?? [];
    arr.push(k);
    acc.set(info.procedureType, arr);
  }
  return Array.from(acc.entries()).map(([procedureType, qualities]) => ({
    procedureType,
    qualities,
  }));
}
